import { datadogLogs } from '@datadog/browser-logs';
import { DisplayAmount } from '@imprint-payments/imprint-lib';
import type { API } from '@imprint-payments/types';
import { Dispatch } from '@reduxjs/toolkit';

import { EVENT, PKG } from '../analytics/analyticsConsts';
import { setConfirm, setRedirectToLogin } from '../app/acceptScreenSlice';
import { setDeviceID } from '../app/apiScreenSlice';
import {
  setEligibilityVerified,
  setIsSubmittingApp,
  setLoading,
} from '../app/applicationScreenSlice';
import {
  setApplicationUUID,
  setExpiresAt,
  setFrozenExpiresAt,
  setProductAccountUUID,
  setRejectionReasonsURL,
  setShowCounterOffer,
} from '../app/applicationStatusSlice';
import {
  setAuth,
  setCardOffer,
  setHHEmailVerification,
  setRewardsEmail,
} from '../app/consumerInfoSlice';
import {
  CardDesignOption,
  Color,
  setCardBackImg,
  setCardDesignOptionImages,
  setCardImg,
  setCardOfferImg,
  setCheckApplicationQR,
  setColor,
  setConfirmLaterQR,
  setCounterOffer,
  setDownloadAppQR,
  setErrorQR,
  setLogoIcon,
  setManualReviewQR,
  setProductUUID,
  setRewardDetails,
  setSuccessQR,
} from '../app/merchantDataSlice';
import { setVirtualCard } from '../app/productAccountSlice';
import { SCREEN, setScreen } from '../app/screenStateSlice';
import { AppDispatch, RootState, store } from '../app/store';
import { ANALYTICS } from '../firebase/firebase';
import { getReferralCode } from '../lib/referralCodeManager';
import { getEmployeeId, getStoreNumber, getUtmSource } from '../lib/utmManager';
import { CardOffer, ConsumerFactors, ConsumerProfile } from '../types';
import { isValidCard } from '../types/Card';
import { reApplicationSetUpAfterPrompt } from '../utils/functionUtils';
import {
  ApplicationEligibilityRequest,
  METHOD,
  RejectionPDFRequest,
  Request,
  requestAssests,
} from '../utils/http';
import { LanguageCode } from '../utils/language';
import { type SetSDKResult } from '../utils/sdk';
import { SESSION_STORAGE } from '../utils/sessionStorageConstants';
import { SIGN_UP_ELIGIBILITY_STATUS, URL } from './apiConstants';
import { handleDisplayState } from './handleDisplayState';
import { getBaseUrl } from './http/getRequestMetadata';
import { RequestType } from './http/httpConsts';

export const generateNewUUID = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

const newUUID =
  Date.now().toString(36) + Math.random().toString(36).substring(2);

export const showError = (dispatch: Dispatch) => {
  dispatch(setLoading(false));
  dispatch(setScreen(SCREEN.ERROR));
};

interface AnalyticsParams {
  analyticsRequestData: string;
}

export const recordAnalyticsToSnowflake = async ({
  analyticsRequestData,
}: AnalyticsParams) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const data = await Request({
      url: URL.ANALYTICS,
      method: METHOD.POST,
      body: JSON.parse(analyticsRequestData),
      keepAlive: true,
    });
    return [null, null];
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    return [null, error as {}];
  }
};

interface PromptParams {
  productUUID: string;
  deviceID: string;
}

export interface PromptResponse {
  signUpEligibilityStatus: string;
  merchantName: string;
  cardName: string;
  cardType: string;
  consumer?: ConsumerProfile;
  consumerFactors?: ConsumerFactors;
  frozenExpiresAt?: string;
  mostRecentApplicationUUID?: string;
  showCounterOffer?: boolean;
}

export const prompt = async ({
  productUUID,
  deviceID,
}: PromptParams): Promise<[PromptResponse | null, PromptResponse | null]> => {
  try {
    const token = store.getState().consumerInfoSelector.auth.token;
    const data = await Request({
      url: `${URL.PROMPT}?productUUID=${productUUID}`,
      method: METHOD.GET,
      token: token,
      deviceID,
    });
    return [data as PromptResponse, null];
  } catch (error) {
    datadogLogs.logger.error('prompt error');
    return error as [null, PromptResponse];
  }
};

export const callPrompt = () => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(setLoading(true));
    const productUUID = getState().merchantDataSelector.productUUID;

    if (getState().apiScreenScreenSelector.deviceID === '') {
      dispatch(setDeviceID(newUUID));
    }

    const deviceID = getState().apiScreenScreenSelector.deviceID;

    const [promptData, promptError] = await prompt({
      productUUID,
      deviceID,
    });

    dispatch(setLoading(false));

    if (promptError) {
      showError(dispatch);
      ANALYTICS.funcError(PKG, 'prompt', JSON.stringify(promptError));
      return promptError;
    }

    if (!promptData) {
      datadogLogs.logger.error('missing prompt data');
      ANALYTICS.funcError(PKG, 'prompt', 'missing prompt data');
      showError(dispatch);
      return null;
    }
    if (promptData.showCounterOffer) {
      dispatch(setShowCounterOffer(promptData.showCounterOffer));
    }

    switch (promptData.signUpEligibilityStatus) {
      case SIGN_UP_ELIGIBILITY_STATUS.REJECTED_WITHIN_COOLDOWN_WINDOW: {
        if (promptData.mostRecentApplicationUUID) {
          dispatch(setApplicationUUID(promptData.mostRecentApplicationUUID));
        }
        ANALYTICS.logEvent(EVENT.APPLICATION_DECISION_DENIED_VIEWED, {
          product_uuid: productUUID,
        });
        dispatch(setScreen(SCREEN.APPLICATION_NOT_APPROVED));
        break;
      }
      case SIGN_UP_ELIGIBILITY_STATUS.REJECTED_AFTER_COOLDOWN_WINDOW: {
        if (promptData.mostRecentApplicationUUID) {
          dispatch(setApplicationUUID(promptData.mostRecentApplicationUUID));
        }

        ANALYTICS.logEvent(
          EVENT.APPLICATION_DECISION_REJECTED_AFTER_COOLDOWN_WINDOW_APPLICATION_PG_VIEWED,
          {
            product_uuid: productUUID,
          }
        );
        reApplicationSetUpAfterPrompt(promptData, dispatch, getState);
        dispatch(setScreen(SCREEN.APPLICATION));
        break;
      }
      case SIGN_UP_ELIGIBILITY_STATUS.ELIGIBLE:
      case SIGN_UP_ELIGIBILITY_STATUS.REAPPLY: {
        const eventName =
          promptData.signUpEligibilityStatus ===
          SIGN_UP_ELIGIBILITY_STATUS.REAPPLY
            ? EVENT.APPLICATION_DECISION_REAPPLY_APPLICATION_PG_VIEWED
            : EVENT.APPLICATION_DECISION_ELIGIBLE_APPLICATION_PG_VIEWED;
        ANALYTICS.logEvent(eventName, {
          product_uuid: productUUID,
        });
        reApplicationSetUpAfterPrompt(promptData, dispatch, getState);
        dispatch(setScreen(SCREEN.APPLICATION));
        break;
      }
      case SIGN_UP_ELIGIBILITY_STATUS.FROZEN: {
        reApplicationSetUpAfterPrompt(promptData, dispatch, getState);
        ANALYTICS.logEvent(EVENT.APPLICATION_DECISION_FROZEN_VIEWED, {
          product_uuid: productUUID,
        });
        dispatch(setFrozenExpiresAt(promptData.frozenExpiresAt!));
        dispatch(setExpiresAt(promptData.frozenExpiresAt!));
        dispatch(setScreen(SCREEN.CREDIT_FROZEN));
        break;
      }
      default: {
        dispatch(setScreen(SCREEN.ACCOUNT_PENDING));
        break;
      }
    }
    return;
  };
};

export const consumerLogOut = async () => {
  const token = store.getState().consumerInfoSelector.auth.token;
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const data = await Request({
      url: `${URL.CONSUMER_LOGOUT}`,
      method: METHOD.POST,
      token: token,
    });
  } catch (error) {
    return [null, error as PhysicalCardResponse];
  }
};

interface GetOfferDetailsRequest {
  applicationUUID: string;
}

interface GetOfferDetailsResponse {
  application?: Application;
  offerConfigUUID?: string;
}

export const getOfferDetails = ({
  applicationUUID,
}: GetOfferDetailsRequest) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoading(true));
    const auth = getState().consumerInfoSelector.auth;
    const token = auth.token;
    if (getState().apiScreenScreenSelector.deviceID === '') {
      dispatch(setDeviceID(newUUID));
    }

    const deviceID = getState().apiScreenScreenSelector.deviceID;

    const [offerDetailsResponse, offerDetailsError] = await offer(
      applicationUUID,
      token,
      deviceID
    );

    if (offerDetailsResponse) {
      dispatch(setCardOffer(offerDetailsResponse.application?.cardOffer));
      dispatch(
        setExpiresAt(offerDetailsResponse.application?.decision.expiresAt || '')
      );
      if (offerDetailsResponse.offerConfigUUID) {
        sessionStorage.setItem(
          SESSION_STORAGE.OFFER_CONFIG_UUIDS,
          offerDetailsResponse.offerConfigUUID
        );
      }
    }
    if (offerDetailsError || !offerDetailsResponse) {
      if (offerDetailsError) {
        ANALYTICS.funcError(
          PKG,
          'offerDetailsResponse',
          JSON.stringify(offerDetailsError)
        );
      }
      dispatch(setLoading(false));
      return;
    }
    dispatch(setLoading(false));
  };
};

export const offer = async (
  applicationUUID: string,
  token: string,
  deviceID: string
): Promise<
  [GetOfferDetailsResponse | null, GetOfferDetailsResponse | null]
> => {
  try {
    const data = await Request({
      url: `${URL.APPS}/${applicationUUID}${URL.OFFER}`,
      token,
      deviceID,
    });
    return [data as GetOfferDetailsResponse, null];
  } catch (error) {
    ANALYTICS.funcError(PKG, 'offer', JSON.stringify(error));
    return [null, error as GetOfferDetailsResponse];
  }
};

export const setOfferPageForCard = (
  productUUID: string,
  applicationUUID: string
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const auth = getState().consumerInfoSelector.auth;

    if (getState().apiScreenScreenSelector.deviceID === '') {
      dispatch(setDeviceID(newUUID));
    }

    dispatch(setProductUUID(productUUID));
    dispatch(setAuth({ ...auth, applicationUUID: applicationUUID }));
    await dispatch(getOfferDetails({ applicationUUID }));
  };
};

export interface PhysicalCard {
  cardUUID: string;
  issuerCardID: string;
  issuerName: string;
  last4: string;
  status: string;
}

export enum PRODUCT_TYPE {
  CREDITCARD = 'CREDITCARD',
  SPENDCARD = 'SPENDCARD',
}

export enum REAPPLICATION_ACCOUNT_STATUS {
  FROZEN = 'FROZEN',
  EXPIRED_OFFER = 'EXPIRED_OFFER',
}

export interface PaymentRequirements {
  minimum: DisplayAmount;
  statement: DisplayAmount;
  current: DisplayAmount;
  interest: DisplayAmount;
  maxScheduleDateEpochSec: number;
}

export interface CreditBalances {
  currentBalance: DisplayAmount;
  paymentRequirements: Record<string, PaymentRequirements>;
}

export interface Rewards {
  email: string;
}

interface InitialSCParams {
  rewardsInfo?: Rewards;
  crossSignupType?: string;
  consumer: ConsumerProfile;
  consumerFactors: ConsumerFactors;
  productUUID: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  offerConfigUUIDs?: string[];
  storeNumber?: string;
  employeeId?: string;
  proveSessionId: string;
  wasPrefilled: boolean;
  SSADisclosureAccepted: boolean;
  referralCode?: string;
}

export interface Decision {
  expiresAt: string;
  message: string;
  remainingDays: string;
  status: string;
}

export interface Application {
  applicationUUID: string;
  decision: Decision;
  cardOffer: CardOffer;
  cardDesignOptions: CardDesignOption[];
}

interface InitialSCRequest {
  token: string;
  deviceID?: string;
  initialScParams?: InitialSCParams;
  applicationUUID?: string;
  ssn9?: string;
  // prove specific params
  wasPrefilled?: boolean;
  proveSessionId?: string;
}

interface InitialSCResponse {
  application?: Application;
  decision?: Decision;
  displayState?: string;
  signUpStatus?: string;
  code?: string;
  message?: string;
  showCounterOffer?: boolean;
}

export const initialSC = async (
  requestParams: InitialSCRequest
): Promise<[InitialSCResponse | null, InitialSCResponse | null]> => {
  try {
    const data = await Request({
      url: requestParams.applicationUUID
        ? `${URL.V2_APPLICATION_SC}/${requestParams.applicationUUID}/submit`
        : URL.V3_APPLICATION_SC,
      method: METHOD.POST,
      body: requestParams.ssn9
        ? { ssn9: requestParams.ssn9 }
        : requestParams.initialScParams,
      token: requestParams.token,
      deviceID: requestParams.deviceID,
    });

    return [data as InitialSCResponse, null];
  } catch (error) {
    return [null, error as InitialSCResponse];
  }
};

interface SubmitAPPParams {
  applicationUUID: string;
  ssn9: string;
  token: string;
}

// POST /v2/application/sc/{applicationUUID}/submit
export const submitAppWithSSN9 = (
  submit: SubmitAPPParams,
  setResult: SetSDKResult
) => {
  return async (dispatch: Dispatch, _getState: () => RootState) => {
    dispatch(setLoading(true));
    dispatch(setIsSubmittingApp(true));

    const [initialSCData, initialSCError] = await initialSC({
      token: submit.token,
      ssn9: submit.ssn9,
      applicationUUID: submit.applicationUUID,
    });

    if (
      initialSCError ||
      !initialSCData?.displayState ||
      !initialSCData?.application
    ) {
      ANALYTICS.logEvent(EVENT.ERROR_VIEWED, {
        error_type: JSON.stringify(initialSCError),
      });
      ANALYTICS.funcError(
        PKG,
        'submitAPP > initialSC',
        JSON.stringify(initialSCError)
      );
      datadogLogs.logger.error('submitAPP > initialSC error', {
        error: initialSCError,
      });
      showError(dispatch);
      return initialSCError;
    }

    handleDisplayState({
      displayState: initialSCData?.displayState,
      application: initialSCData?.application,
      showCounterOffer: initialSCData.showCounterOffer,
      token: submit.token,
      setResult,
    });
    dispatch(setIsSubmittingApp(false));
  };
};

interface SubmitApplicationParams {
  rewardsInfo?: Rewards;
  consumer: ConsumerProfile;
  consumerFactor: ConsumerFactors;
  productUUID: string;
  isMultiCard?: boolean;
  offerUUIDs?: string[];
  setResult: SetSDKResult;
  SSADisclosureAccepted: boolean;
}

export const submitApplication = ({
  rewardsInfo,
  consumer,
  consumerFactor,
  productUUID,
  isMultiCard,
  offerUUIDs,
  setResult,
  SSADisclosureAccepted,
}: SubmitApplicationParams) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoading(true));
    dispatch(setIsSubmittingApp(true));
    const auth = getState().consumerInfoSelector.auth;
    const token = auth.token;
    const utmSource = getUtmSource(productUUID);
    const utmMedium = sessionStorage.getItem(SESSION_STORAGE.UTM_MEDIUM)!;
    const utmCampaign = sessionStorage.getItem(SESSION_STORAGE.UTM_CAMPAIGN)!;
    const utmContent = sessionStorage.getItem(SESSION_STORAGE.UTM_CONTENT)!;
    const utmTerm = sessionStorage.getItem(SESSION_STORAGE.UTM_TERM)!;
    const promoCode = sessionStorage.getItem(SESSION_STORAGE.PRM)!;
    const storeNumber = getStoreNumber(productUUID) || undefined;
    const employeeId = getEmployeeId(productUUID) || undefined;
    if (getState().apiScreenScreenSelector.deviceID === '') {
      dispatch(setDeviceID(newUUID));
    }
    const deviceID = getState().apiScreenScreenSelector.deviceID;

    const proveStatus = getState().applicationScreenSelector.proveStatus;
    const proveParams = {
      proveSessionId: getState().applicationScreenSelector.proveSessionId,
      wasPrefilled: proveStatus === 'success' || proveStatus === 'noEmail',
    } as const;

    const referralCode = getReferralCode(productUUID) || undefined;

    let initialSCData;
    let initialSCError;
    if (isMultiCard) {
      if (offerUUIDs?.length === 0) {
        [initialSCData, initialSCError] = await initialSC({
          token: token,
          deviceID: deviceID,
          initialScParams: {
            rewardsInfo: rewardsInfo,
            consumer: consumer,
            consumerFactors: consumerFactor,
            productUUID: productUUID,
            utmSource: utmSource,
            utmMedium: utmMedium,
            utmCampaign: utmCampaign,
            utmContent: utmContent,
            utmTerm: utmTerm,
            storeNumber: storeNumber,
            employeeId: employeeId,
            crossSignupType: 'CC_APPLY_CC',
            referralCode,
            ...proveParams,
            SSADisclosureAccepted,
          },
        });
      } else {
        [initialSCData, initialSCError] = await initialSC({
          token: token,
          deviceID: deviceID,
          initialScParams: {
            rewardsInfo: rewardsInfo,
            consumer: consumer,
            consumerFactors: consumerFactor,
            productUUID: productUUID,
            utmSource: utmSource,
            utmMedium: utmMedium,
            utmCampaign: utmCampaign,
            utmContent: utmContent,
            utmTerm: utmTerm,
            storeNumber: storeNumber,
            employeeId: employeeId,
            crossSignupType: 'CC_APPLY_CC',
            offerConfigUUIDs: offerUUIDs,
            referralCode,
            ...proveParams,
            SSADisclosureAccepted,
          },
        });
      }
    } else {
      if (offerUUIDs?.length == 0) {
        [initialSCData, initialSCError] = await initialSC({
          token: token,
          initialScParams: {
            rewardsInfo: rewardsInfo,
            consumer: consumer,
            consumerFactors: consumerFactor,
            productUUID: productUUID,
            utmSource: utmSource,
            utmMedium: utmMedium,
            utmCampaign: utmCampaign,
            utmContent: utmContent,
            utmTerm: utmTerm,
            storeNumber: storeNumber,
            employeeId: employeeId,
            referralCode,
            ...proveParams,
            SSADisclosureAccepted,
          },
        });
      } else {
        [initialSCData, initialSCError] = await initialSC({
          token: token,
          initialScParams: {
            rewardsInfo: rewardsInfo,
            consumer: consumer,
            consumerFactors: consumerFactor,
            productUUID: productUUID,
            utmSource: utmSource,
            utmMedium: utmMedium,
            utmCampaign: utmCampaign,
            utmContent: utmContent,
            utmTerm: utmTerm,
            storeNumber: storeNumber,
            employeeId: employeeId,
            offerConfigUUIDs: offerUUIDs,
            referralCode,
            ...proveParams,
            SSADisclosureAccepted,
          },
        });
      }
    }

    if (initialSCError) {
      datadogLogs.logger.error('submitApplication > initialSCError', {
        error: initialSCError,
      });
      ANALYTICS.logEvent(EVENT.ERROR_VIEWED, {
        error_type: (initialSCError as any)?.message,
      });
      ANALYTICS.funcError(
        PKG,
        'submitApplication > initialSC',
        JSON.stringify(initialSCError)
      );
      showError(dispatch);
      return initialSCError;
    }

    dispatch(
      setAuth({
        ...auth,
        token: token,
        applicationUUID: initialSCData!.application!.applicationUUID!,
      })
    );
    if (promoCode) {
      ANALYTICS.logEvent(EVENT.SUBMIT_APPLICATION_WITH_REFERRAL_CODE, {
        applicationUUID: initialSCData!.application!.applicationUUID!,
        promo_code: promoCode,
      });
    }
    dispatch(setLoading(false));

    if (
      !initialSCData ||
      !initialSCData.application ||
      !initialSCData.displayState
    ) {
      ANALYTICS.funcError(
        PKG,
        'SubmitApplication',
        'missing response data for initSC'
      );
      return;
    }
    handleDisplayState({
      displayState: initialSCData?.displayState,
      application: initialSCData?.application,
      showCounterOffer: initialSCData.showCounterOffer,
      token: token,
      isMultiCard: isMultiCard,
      deviceID: deviceID,
      setResult,
    });
    dispatch(setIsSubmittingApp(false));
  };
};

export const getVirtualCard = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/mobile-assets/light/virtualCard.png`,
        image: true,
      })) as string;
      dispatch(setCardImg(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getVirtualCardUrl = (productUUID: string) => {
  if (!productUUID) {
    return '';
  }
  return `${getBaseUrl(
    store,
    RequestType.EXTERNAL
  )}/content/${productUUID}/mobile-assets/light/virtualCard.png`;
};

export const getCardBackImg = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/cardBack.svg`,
        image: true,
      })) as string;
      dispatch(setCardBackImg(data));
    } catch (error) {
      ANALYTICS.funcError(PKG, 'getCardBackImg', String(error));
    }
  };
};

export const getCardOfferImg = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/offerImage.png`,
        image: true,
      })) as string;
      dispatch(setCardOfferImg(data));
    } catch (error) {
      ANALYTICS.funcError(PKG, 'getCardOfferImg', JSON.stringify(error));
    }
  };
};

interface Amounts {
  amount: string;
  amountSubtext: string;
  title: string;
  items: Items;
  type: string;
}

interface Items {
  text: string;
}

interface RewardDetailsResponse {
  merchantName: string;
  cardName: string;
  termCTA: string;
  rewardsType: string;
  hideHowCardWorksPage2: false;
  hideHowCardWorksPage3: true;
  signupBonus: Amounts;
  rewards: [Amounts, Amounts];
  rewardsBonus: Array<Amounts>;
  rewardsNote: string;
  primaryValueProp: string;
}

export const getRewardDetails = (productUUID: string, language: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url:
          language === LanguageCode.English
            ? `/content/${productUUID}/program-details/rewards.json`
            : `/content/${productUUID}/program-details/es/rewards.json`,
      })) as RewardDetailsResponse;
      dispatch(setRewardDetails(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getColor = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/mobile-assets/colors.json`,
      })) as Color;
      dispatch(setColor(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getLogoIcon = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/logoIcon.svg`,
        image: true,
      })) as string;
      dispatch(setLogoIcon(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

interface ConfirmRequest {
  token: string;
  applicationUUID: string;
  cardDesignID?: string;
  deviceID?: string;
  sendSms?: boolean;
  onSuccess?: () => void;
  onFail?: () => void;
}

interface ConfirmResponse {
  cardUUID: string;
  cardName: string;
  status: string;
  last4: string;
  issuerName: string;
  issuerCardID: string;
  physicalCardPromptType?: string;
  address: string;
  productAccountUUID: string;
}

export const confirmApi = async ({
  token,
  applicationUUID,
  cardDesignID,
  deviceID,
  sendSms,
}: ConfirmRequest) => {
  try {
    const data = await Request({
      url: `/v2/applications/${applicationUUID}/confirm`,
      method: METHOD.POST,
      body: {
        cardDesignID,
        sendSms,
      },
      token,
      deviceID,
    });
    return [data as ConfirmResponse, null];
  } catch (error) {
    return [null, error as ConfirmResponse];
  }
};

export const confirm = ({
  token,
  applicationUUID,
  cardDesignID,
  sendSms,
  onSuccess,
  onFail,
}: ConfirmRequest) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoading(true));
    if (getState().apiScreenScreenSelector.deviceID === '') {
      dispatch(setDeviceID(newUUID));
    }

    const deviceID = getState().apiScreenScreenSelector.deviceID;

    let confirmResponse: ConfirmResponse | null;
    let confirmError;

    // eslint-disable-next-line prefer-const
    [confirmResponse, confirmError] = await confirmApi({
      token,
      applicationUUID,
      cardDesignID,
      deviceID,
      sendSms,
    });

    if (confirmError || !confirmResponse) {
      if (confirmError) {
        datadogLogs.logger.error('confirm error', confirmError);
      }
      if (!confirmResponse) {
        datadogLogs.logger.error('no confirm response');
      }
      ANALYTICS.logEvent(EVENT.ERROR_VIEWED, {
        error_type: (confirmError as any)?.message,
      });
      ANALYTICS.funcError(
        PKG,
        'confirm > confirmError',
        JSON.stringify(confirmError)
      );
      if (onFail) {
        onFail();
      }
      showError(dispatch);
      return;
    }

    try {
      type LoadProductAccountResponse = {
        body: API.Response<'/v2/productAccount/{productAccountUUID}', 'get'>;
      };

      const fetchProductAccountWithRetry = async (
        maxAttempts = 10,
        delayMs = 1000
      ) => {
        let attempts = 0;
        let productAccount;

        while (attempts < maxAttempts) {
          const res = (await Request({
            url: `${URL.LOAD_PRODUCT_ACCOUNT}/${
              confirmResponse!.productAccountUUID
            }`,
            method: METHOD.GET,
            token,
            deviceID,
            headers: true,
          })) as LoadProductAccountResponse;

          productAccount = res.body;

          if (isValidCard(productAccount.virtualCard)) {
            return productAccount;
          }

          attempts++;
          if (attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }

        return productAccount;
      };

      const productAccount = await fetchProductAccountWithRetry();

      if (isValidCard(productAccount?.virtualCard)) {
        dispatch(setVirtualCard(productAccount!.virtualCard));
      }

      ANALYTICS.logEvent(EVENT.PRODUCT_ACCOUNT_LOAD, {
        product_account_uuid: confirmResponse.productAccountUUID,
      });
    } catch (err) {
      ANALYTICS.logEvent(EVENT.PRODUCT_ACCOUNT_LOAD_ERROR, {
        product_account_uuid: confirmResponse.productAccountUUID,
        error: `Error fetching product account data: ${err}`,
      });
    }

    dispatch(setProductAccountUUID(confirmResponse.productAccountUUID));
    dispatch(setLoading(false));
    dispatch(setConfirm(true));
    if (onSuccess) {
      onSuccess();
    }
  };
};

interface CheckEligibilityParams {
  productUUID: string;
  email: string;
}

export const checkEligibility = ({
  productUUID,
  email,
}: CheckEligibilityParams) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoading(true));
    const auth = getState().consumerInfoSelector.auth;
    const token = auth.token;

    const [eligibilityResponse, eligibilityError] = await eligibility(
      productUUID,
      email,
      token
    );

    if (eligibilityError) {
      ANALYTICS.logEvent(EVENT.ERROR_VIEWED, {
        error_type: (eligibilityError as any)?.message,
      });
      ANALYTICS.funcError(
        PKG,
        'checkEligibility > eligibilityError',
        JSON.stringify(eligibilityError)
      );
      datadogLogs.logger.error('eligibility error', eligibilityError);
      showError(dispatch);
      return eligibilityError;
    }

    if (eligibilityResponse?.eligible) {
      dispatch(setRewardsEmail(email));
      dispatch(setEligibilityVerified(true));
      dispatch(setLoading(false));
      dispatch(setHHEmailVerification(false));
      return;
    }
    dispatch(setLoading(false));
    dispatch(setHHEmailVerification(true));
  };
};

interface EligibilityResponse {
  eligible?: string;
}

export const eligibility = async (
  productUUID: string,
  email: string,
  token: string
): Promise<[EligibilityResponse | null, EligibilityResponse | null]> => {
  try {
    const data = await ApplicationEligibilityRequest({
      url: URL.APPLICATIONS,
      token,
      productUUID,
      email,
    });
    return [data as EligibilityResponse, null];
  } catch (error) {
    return [null, error as EligibilityResponse];
  }
};

interface GetRejectionReasonsRequest {
  applicationUUID: string;
}

interface GetRejectionReasonsResponse {
  fileURL?: string;
}

export const getRejectionReasons = ({
  applicationUUID,
}: GetRejectionReasonsRequest) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoading(true));
    const auth = getState().consumerInfoSelector.auth;
    const token = auth.token;

    const [rejectionPDFResponse, rejectionPDFError] = await rejectionPDF(
      applicationUUID,
      token
    );

    if (rejectionPDFError) {
      ANALYTICS.logEvent(EVENT.ERROR_VIEWED, {
        error_type: (rejectionPDFError as any)?.message,
      });
      ANALYTICS.funcError(
        PKG,
        'getRejectionReasons > rejectionPDFError',
        JSON.stringify(rejectionPDFError)
      );
      datadogLogs.logger.error('rejection pdf error', rejectionPDFError);
      showError(dispatch);
      return rejectionPDFError;
    }

    dispatch(setRejectionReasonsURL(rejectionPDFResponse?.fileURL || ''));

    dispatch(setLoading(false));
  };
};

export const rejectionPDF = async (
  applicationUUID: string,
  token: string
): Promise<
  [GetRejectionReasonsResponse | null, GetRejectionReasonsResponse | null]
> => {
  try {
    const data = await RejectionPDFRequest({
      url: URL.APPS,
      applicationUUID,
      token,
    });
    return [data as GetRejectionReasonsResponse, null];
  } catch (error) {
    return [null, error as GetRejectionReasonsResponse];
  }
};

export const getSuccessQR = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/successQR.svg`,
        image: true,
      })) as string;
      dispatch(setSuccessQR(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getCheckApplicationQR = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/checkApplicationQR.svg`,
        image: true,
      })) as string;
      dispatch(setCheckApplicationQR(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getErrorQR = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/errorQR.svg`,
        image: true,
      })) as string;
      dispatch(setErrorQR(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getConfirmLaterQR = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/confirmLaterQR.svg`,
        image: true,
      })) as string;
      dispatch(setConfirmLaterQR(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getManualReviewQR = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/manualReviewQR.svg`,
        image: true,
      })) as string;
      dispatch(setManualReviewQR(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getDownloadAppQRCode = (productUUID: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const data = (await requestAssests({
        url: `/content/${productUUID}/web-assets/downloadAppQRCode.svg`,
        image: true,
      })) as string;
      dispatch(setDownloadAppQR(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

export const getCardDesignOptions = (
  cardDesignID: string,
  productUUID: string
) => {
  return async (dispatch: Dispatch) => {
    try {
      const physicalData = (await requestAssests({
        url: `/content/${productUUID}/web-assets/card-designs/physical/${cardDesignID}.svg`,
        image: true,
      })) as string;

      const renderData = (await requestAssests({
        url: `/content/${productUUID}/web-assets/card-designs/render/${cardDesignID}.svg`,
        image: true,
      })) as string;

      const data = {
        cardDesignID: cardDesignID,
        physical: physicalData,
        render: renderData,
      };

      dispatch(setCardDesignOptionImages(data));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // TODO: do something with this error
    }
  };
};

interface PhysicalCardResponse {
  cardUUID: string;
  status: string;
}

export const requestPhysicalCard = (
  token: string,
  productAccountUUID: string
) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setRedirectToLogin(true));

    const [requestPhysicalCardResponse, requestPhysicalCardError] =
      await requestPhysicalCardHelper(
        productAccountUUID,
        CardType.PHYSICAL,
        token
      );

    if (requestPhysicalCardError || !requestPhysicalCardResponse) {
      if (requestPhysicalCardError) {
        ANALYTICS.funcError(
          PKG,
          'requestPhysicalCard',
          JSON.stringify(requestPhysicalCardError)
        );
      }
      return;
    }
    dispatch(setLoading(false));
  };
};

enum CardType {
  PHYSICAL = 'PHYSICAL',
  VIRTUAL = 'VIRTUAL',
}

export const requestPhysicalCardHelper = async (
  productAccountUUID: string,
  cardType: CardType,
  token: string
): Promise<[PhysicalCardResponse | null, PhysicalCardResponse | null]> => {
  try {
    const data = await Request({
      url: `${URL.CARD_ENDPOINT}`,
      method: METHOD.POST,
      body: {
        productAccountUUID,
        cardType,
      },
      token: token,
    });
    return [data as PhysicalCardResponse, null];
  } catch (error) {
    return [null, error as PhysicalCardResponse];
  }
};

interface CounterOfferResponse {
  title: string;
  titleHighlight: string;
  subtitle: string;
  imageURLPath: string;
  lightModeColor: string;
  darkModeColor: string;
  hyperlinkButtonName: string;
  url: string;
  disclosure: string;
}

export const getCounterOffer = (productUUID: string, language: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const counterOfferImg = (await requestAssests({
        url: `/content/${productUUID}/web-assets/counterOffer.png`,
        image: true,
      })) as string;

      const counterOfferJSON = (await requestAssests({
        url:
          language === LanguageCode.English
            ? `/content/${productUUID}/program-details/counterOffer.json`
            : `/content/${productUUID}/program-details/es/counterOffer.json`,
      })) as CounterOfferResponse;

      const counterOffer = {
        title: counterOfferJSON.title,
        titleHighlight: counterOfferJSON.titleHighlight,
        subtitle: counterOfferJSON.subtitle,
        image: counterOfferImg,
        hyperlinkButtonName: counterOfferJSON.hyperlinkButtonName,
        url: counterOfferJSON.url,
        disclosure: counterOfferJSON.disclosure,
        lightModeColor: counterOfferJSON.lightModeColor,
        darkModeColor: counterOfferJSON.darkModeColor,
      };

      dispatch(setCounterOffer(counterOffer));
    } catch (error) {
      ANALYTICS.funcError(PKG, 'getCounterOffer', JSON.stringify(error));
      dispatch(setShowCounterOffer(false));
    }
  };
};
